import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { DateTime } from 'luxon';

/**
 * ENF-2372 (Épica A) — reglas puras de las ediciones de un Máster que el modal
 * refleja en cliente. El servidor es la autoridad: recalcula el nombre y vuelve
 * a validar número y vigencia.
 */

/** Límite del nombre del curso. Debe coincidir con `course.name` (STRING(100)) en BD. */
export const COURSE_NAME_MAX_LENGTH = 100;

/**
 * Longitud en caracteres (puntos de código), igual que la cuenta el servidor y PostgreSQL: un emoji cuenta 1,
 * no 2 como `String.length` (unidades UTF-16) (D-08).
 */
export function codePointLength(value: string | null | undefined): number {
  return value ? Array.from(value).length : 0;
}

/**
 * `maxlength` nativo del campo nombre: holgado (2 × 100 unidades UTF-16) para que el navegador nunca corte en silencio
 * un texto válido, ni al teclear ni al pegar de una vez (E-D1: con emojis, 100 caracteres ocupan hasta 200 unidades).
 * El límite real (100 caracteres) lo impone `maxCodePointsValidator`, que muestra el error y bloquea el guardado.
 */
export const COURSE_NAME_INPUT_MAXLENGTH = COURSE_NAME_MAX_LENGTH * 2;

/**
 * Límite del nombre del Máster (`course.masterName`, ENF-2372 v2, D1): deja sitio a « - 1ª Edición» y siguientes
 * dentro de los 100 caracteres de `course.name`. El servidor lo vuelve a validar al crear y al renombrar.
 */
export const MASTER_NAME_MAX_LENGTH = 86;

/** `maxlength` nativo del campo «Nombre del Máster»: holgado por la misma razón que `COURSE_NAME_INPUT_MAXLENGTH`. */
export const MASTER_NAME_INPUT_MAXLENGTH = MASTER_NAME_MAX_LENGTH * 2;

/**
 * Como `Validators.maxLength`, pero contando puntos de código (D-08). Devuelve el mismo error `maxlength` para que el
 * resto del formulario lo trate igual.
 */
export function maxCodePointsValidator(max: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (typeof value !== 'string') return null;
    const actualLength = codePointLength(value);
    return actualLength > max ? { maxlength: { requiredLength: max, actualLength } } : null;
  };
}

/** Clave del servidor para «el curso se borró mientras su formulario estaba abierto» (QA D-04). */
export const COURSE_DELETED_ERROR_KEY = 'course.cannotSaveDeletedCourse';

/**
 * Textos con los que el servidor responde `course.cannotSaveDeletedCourse` (`enfaf-api/src/i18n/i18n.ts`, ES y EN).
 * El cuerpo del error es solo `{ message }` (sin `field` ni clave), así que se reconoce por el texto; si el servidor
 * llega a enviar la clave (`code`/`key`), se usa esa.
 */
const COURSE_DELETED_SERVER_MESSAGES = [
  'Este curso se ha eliminado y ya no se puede guardar. Cierra el formulario y recarga el listado',
  'This course has been deleted and can no longer be saved. Close the form and reload the list'
];

/** El guardado falló porque el curso ya está borrado (400 `course.cannotSaveDeletedCourse`). */
export function isCourseDeletedError(serverError: { message?: unknown; code?: unknown; key?: unknown } | null | undefined): boolean {
  if (!serverError) return false;
  if (serverError.code === COURSE_DELETED_ERROR_KEY || serverError.key === COURSE_DELETED_ERROR_KEY) return true;
  return typeof serverError.message === 'string' && COURSE_DELETED_SERVER_MESSAGES.includes(serverError.message.trim());
}

/** La vigencia se introduce y se muestra en hora de Madrid (A.3). */
export const EDITION_TIME_ZONE = 'Europe/Madrid';

/**
 * `{rootName} - {n}ª Edición` (A.8, formato confirmado por el PO). Es un dato del
 * curso que genera también el servidor, no un texto de interfaz: no se traduce.
 */
export function buildEditionName(rootName: string, editionNumber: number): string {
  return `${rootName.trim()} - ${editionNumber}ª Edición`;
}

/** Sufijo que el servidor añade a cada curso de un Máster («… - 3ª Edición»). */
const EDITION_NAME_SUFFIX = /\s+-\s+\d+ª Edición$/;

/**
 * Nombre del Máster a partir del nombre completo de uno de sus cursos. Solo es un respaldo para cuando el servidor
 * no envía `masterName` (ENF-2372 v2): el nombre del Máster bueno es siempre el de la raíz.
 */
export function stripEditionSuffix(name: string | null | undefined): string | null {
  if (!name) return null;
  return name.replace(EDITION_NAME_SUFFIX, '').trim() || null;
}

export function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

/**
 * Número de edición tal y como lo deja el campo. `enfaf-input` enlaza `[type]` de forma
 * dinámica, así que Angular usa el accesor por defecto y el valor llega como texto
 * («5»); se convierte a número. `null` si está vacío, `NaN` si no es numérico.
 */
export function parseEditionNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  const text = String(value).trim();
  if (!text) return null;
  return /^[-+]?\d+(?:[.,]\d+)?$/.test(text) ? Number(text.replace(',', '.')) : NaN;
}

/** Mayor número de edición que acepta el servidor (`MAX_EDITION_NUMBER`, regla 3 de course-editions.md). */
export const MAX_EDITION_NUMBER = 2147483646;

/**
 * Entero de 1 a `MAX_EDITION_NUMBER` (mismo rango que el servidor, `course.editionNumberInvalid`). Vacío no es error
 * aquí: lo cubre `Validators.required` cuando aplica.
 */
export const positiveIntegerValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = parseEditionNumber(control.value);
  if (value === null) return null;
  return isPositiveInteger(value) && value <= MAX_EDITION_NUMBER ? null : { positiveInteger: true };
};

/** La 1ª edición es la raíz: una edición tiene siempre un número mayor que 1 (CHECK del servidor). */
export const MIN_EDITION_NUMBER = 2;

/**
 * Número de una edición: entero de 2 a `MAX_EDITION_NUMBER` (ENF-2372 v2). Vacío no es error aquí: lo cubre
 * `Validators.required`.
 */
export const editionNumberValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = parseEditionNumber(control.value);
  if (value === null) return null;
  return isPositiveInteger(value) && value >= MIN_EDITION_NUMBER && value <= MAX_EDITION_NUMBER ? null : { editionNumberRange: true };
};

type PickerValue = DateTime | Date | string | null | undefined;

/** Fecha y hora de reloj, sin zona. */
interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

/**
 * Hora de reloj de un valor del selector. Un `DateTime` se lee en SU zona (Madrid si viene del selector de la
 * vigencia, ver `EditionDateAdapter`); un `Date` (otros selectores, que trabajan en la zona del navegador) con los
 * getters locales; un texto ISO en Madrid.
 */
function toWallClock(value: PickerValue): WallClock | null {
  if (value === null || value === undefined || value === '') return null;
  let dateTime: DateTime;
  if (DateTime.isDateTime(value)) {
    dateTime = value;
  } else if (value instanceof Date) {
    dateTime = DateTime.fromJSDate(value);
  } else {
    dateTime = DateTime.fromISO(value, { zone: EDITION_TIME_ZONE });
  }
  if (!dateTime.isValid) return null;
  const { year, month, day, hour, minute } = dateTime;
  return { year, month, day, hour, minute };
}

/**
 * Hora de reloj → instante en Madrid, sin pasar nunca por la zona del navegador (QA-A-03: una hora que no existe en
 * la zona del navegador, p. ej. 14/03/2027 02:30 en Nueva York, ya no se desplaza).
 *
 * Cambios de hora de Madrid (Luxon):
 * - hora inexistente (último domingo de marzo, 02:00–02:59; p. ej. 29/03/2026 02:30): se adelanta lo que dura el
 *   salto, a las 03:30 CEST, la misma hora que marcaría un reloj que no se hubiera adelantado. El campo la muestra
 *   ya corregida (03:30), así que el administrador ve lo que se guarda.
 * - hora repetida (último domingo de octubre, 02:00–02:59; p. ej. 25/10/2026 02:30): se toma la primera de las dos
 *   (CEST, +02:00).
 */
export function madridDateTime(clock: WallClock): DateTime | null {
  const madrid = DateTime.fromObject({ ...clock, second: 0, millisecond: 0 }, { zone: EDITION_TIME_ZONE });
  if (!madrid.isValid) return null;
  // Luxon resuelve la hora repetida con el desfase de hoy: en invierno daría la segunda (CET). Si una hora antes el
  // reloj de Madrid marca lo mismo, esta es la segunda, así que nos quedamos con la primera.
  const anHourBefore = madrid.minus({ hours: 1 });
  return anHourBefore.hour === madrid.hour && anHourBefore.minute === madrid.minute ? anHourBefore : madrid;
}

/**
 * ISO guardado (UTC) → valor del selector: el mismo instante en la zona de Madrid. El selector de la vigencia usa
 * `EditionDateAdapter` (Luxon fijado a Madrid), así que pinta la hora de Madrid esté donde esté el navegador.
 */
export function utcIsoToPickerValue(iso: string | null | undefined): DateTime | null {
  if (!iso) return null;
  const madrid = DateTime.fromISO(iso, { zone: EDITION_TIME_ZONE });
  return madrid.isValid ? madrid : null;
}

/**
 * Valor del selector → ISO en UTC. Su hora de reloj se interpreta en Madrid (ver `toWallClock`): es la inversa
 * exacta de `utcIsoToPickerValue`, de modo que guardar y reabrir no desplaza la hora (QA de A.3).
 */
export function pickerValueToUtcIso(value: PickerValue): string | null {
  const clock = toWallClock(value);
  const madrid = clock ? madridDateTime(clock) : null;
  return madrid ? madrid.toUTC().toISO() : null;
}

/** Formatos que se aceptan al teclear la vigencia (los mismos que `enfaf-datetime-picker-light`). */
const TYPED_DATE_FORMATS: Array<{ pattern: RegExp; order: 'dmy' | 'ymd' }> = [
  { pattern: /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/, order: 'dmy' },
  { pattern: /^(\d{4})-(\d{1,2})-(\d{1,2})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/, order: 'ymd' },
  { pattern: /^(\d{1,2})-(\d{1,2})-(\d{4})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/, order: 'dmy' }
];

/**
 * Texto tecleado en «Inicio/Fin de vigencia» → instante en Madrid (QA-A-03). Se lee año/mes/día/hora/minuto del
 * texto y se construye directamente en `Europe/Madrid` (los segundos se descartan). `null` si no es una fecha válida
 * (31/02, 25:00…): el campo conserva entonces lo que ya hubiera interpretado el selector.
 */
export function parseEditionDateText(text: string | null | undefined): DateTime | null {
  const clean = (text ?? '').trim().replace(/,/g, '');
  if (!clean) return null;
  for (const { pattern, order } of TYPED_DATE_FORMATS) {
    const match = clean.match(pattern);
    if (!match) continue;
    // Sin hora, las 00:00. Los segundos se aceptan pero se descartan.
    const [first, second, third, hour, minute, seconds] = match.slice(1).map((part) => (part === undefined ? 0 : Number(part)));
    const [year, month, day] = order === 'ymd' ? [first, second, third] : [third, second, first];
    if (hour > 23 || minute > 59 || seconds > 59) return null;
    // Luxon rechaza el 31/02 o el mes 13 (isValid = false) en vez de desbordarlos al mes siguiente.
    return madridDateTime({ year, month, day, hour, minute });
  }
  return null;
}

/** Años de vigencia que acepta el servidor (`MIN_VALIDITY_YEAR`/`MAX_VALIDITY_YEAR`, regla 8 de course-editions.md). */
export const MIN_VALIDITY_YEAR = 1970;
export const MAX_VALIDITY_YEAR = 2100;

/** Límites del calendario de la vigencia (en Madrid): 01/01/1970 00:00 y 31/12/2100 23:59. */
export const VALIDITY_MIN_DATE = DateTime.fromObject({ year: MIN_VALIDITY_YEAR, month: 1, day: 1 }, { zone: EDITION_TIME_ZONE });
export const VALIDITY_MAX_DATE = DateTime.fromObject(
  { year: MAX_VALIDITY_YEAR, month: 12, day: 31, hour: 23, minute: 59 },
  { zone: EDITION_TIME_ZONE }
);

/**
 * Año de la vigencia entre 1970 y 2100, como el servidor (D-06): sin esto, «01/01/0001» se guardaba desplazado
 * (desfase LMT) y «31/12/9999» se aceptaba. Se mira el año de reloj en Madrid. Vacío no es error aquí.
 */
export const validityYearValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const clock = toWallClock(control.value);
  if (!clock) return null;
  return clock.year >= MIN_VALIDITY_YEAR && clock.year <= MAX_VALIDITY_YEAR ? null : { validityYearRange: true };
};

function toMillis(value: PickerValue): number | null {
  const iso = pickerValueToUtcIso(value);
  return iso ? DateTime.fromISO(iso).toMillis() : null;
}

/**
 * Se aplica al control «Fin de vigencia»: error `validityRange` si el fin no es
 * posterior al inicio. El solapamiento entre ediciones sí se permite (D-2).
 */
export function validityRangeValidator(fromControlName: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const until = toMillis(control.value);
    const from = toMillis(control.parent?.get(fromControlName)?.value);
    if (until === null || from === null) return null;
    return until > from ? null : { validityRange: true };
  };
}
